/**
 * How a change reads, wherever the editor shows one.
 *
 * Red for what goes, green for what comes, in one place so the written rules
 * and the access groups cannot drift apart. They were two copies of the same
 * four constants, which is how a reader ends up learning the colours twice.
 */
export const DIFF_KIND = {
  same: "same",
  added: "added",
  removed: "removed",
  changed: "changed",
} as const;

export type DiffKind = (typeof DIFF_KIND)[keyof typeof DIFF_KIND];

/** The row's own ground. */
export const TINT: Record<DiffKind, string> = {
  same: "",
  removed: "bg-red-10",
  added: "bg-green-10",
  // A table that changed keeps its own ground. The row tint would colour the
  // whole table, including the rows that did not move; the marks inside the
  // cells say which did.
  changed: "",
};

// The sign only, in the change's own ink. It used to carry a second, darker
// tint of its own, which read as a chip stuck to the side of the row rather
// than a gutter running down the document.
export const GUTTER: Record<DiffKind, string> = {
  same: "text-transparent",
  removed: "text-red-60",
  added: "text-green-60",
  changed: "text-green-60",
};

export const SIGN: Record<DiffKind, string> = {
  same: "",
  removed: "−",
  added: "+",
  changed: "±",
};

/**
 * One step stronger than the row tint under it, so a marked word reads as
 * emphasis within the change rather than a second block of colour.
 *
 * Both this and the row tint sit one step down the ramp from where they
 * started. At 5 the row tint was 96% lightness, near enough to white that a
 * changelog entry nobody wrote read the same as one the assistant had just
 * added; two steps down was loud enough to read as a warning. One step is the
 * quiet version that can still be seen.
 */
export const WORD: Record<DiffKind, string> = {
  same: "",
  removed: "bg-red-20 line-through decoration-red-40",
  added: "bg-green-20",
  changed: "bg-green-20",
};

/** What a word marked as GONE looks like, wherever it appears. */
export const DROPPED = WORD.removed;

/** Cell rings preserve workbook fills and use the same diff colour roles. */
export const CELL_MARK_COLOR = {
  changed: "hsl(var(--gold-60))",
  added: "hsl(var(--green-60))",
  removed: "hsl(var(--red-60))",
  format: "hsl(var(--gray-50))",
} as const;
